import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Input,
  Select,
  Tag,
  Modal,
  Row,
  Col,
  Tooltip,
  Typography,
  Badge,
  List,
  Empty,
  Checkbox,
  message,
  Progress,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilePptOutlined,
  FilePdfOutlined,
  FileOutlined,
  FolderOutlined,
  SwapOutlined,
  LoadingOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';

import { formatCostUSD, formatDateShort, tagColor, mekakApi } from './services';
import type { Knowledge, FileDocument } from './models';
import { useKnowledgeList, useFileUpload, useFolderTree } from './handlers';
import FolderTree from './components/FolderTree';
import FolderBreadcrumb from './components/FolderBreadcrumb';
import MoveToFolderModal from './components/MoveToFolderModal';
import CreateKnowledgeModal from './components/CreateKnowledgeModal';
import KnowledgeDetailModal from './components/KnowledgeDetailModal';
import FileDocumentDetailModal from './components/FileDocumentDetailModal';

const { Search } = Input;
const { Text } = Typography;

const imageTypes = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

const fileTypeIcon = (type: string) => {
  if (type === 'pptx') return <FilePptOutlined style={{ fontSize: 20, color: '#d4380d' }} />;
  if (type === 'pdf') return <FilePdfOutlined style={{ fontSize: 20, color: '#cf1322' }} />;
  if (imageTypes.includes(type)) return <PictureOutlined style={{ fontSize: 20, color: '#1677ff' }} />;
  return <FileOutlined style={{ fontSize: 20 }} />;
};

const KnowledgeList: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    handleSearch,
    handleTagFilter,
  } = useKnowledgeList();

  const folder = useFolderTree();
  const { fileDocuments, loadingFiles, handleDeleteFile } = useFileUpload();

  // Selection state for batch move
  const [selectedKnowledgeIDs, setSelectedKnowledgeIDs] = useState<string[]>([]);
  const [selectedFileDocIDs, setSelectedFileDocIDs] = useState<string[]>([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewKnowledgeId, setViewKnowledgeId] = useState<string | null>(null);
  const [viewFileDocId, setViewFileDocId] = useState<string | null>(null);

  const handleView = (id: string) => setViewKnowledgeId(id);
  const handleEdit = (id: string) => navigate(`/mekak/knowledge/${id}/edit`);
  const handleCreate = () => setCreateModalOpen(true);

  const handleCreated = () => {
    folder.loadTree();
    folder.loadContents(folder.selectedFolderID);
  };

  const handleDelete = (record: Knowledge) => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除知識「${record.title}」嗎？此操作無法復原。`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await mekakApi.deleteKnowledge(record.id);
          message.success('知識刪除成功');
          folder.loadTree();
          folder.loadContents(folder.selectedFolderID);
        } catch (error) {
          message.error(`刪除失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  const handleMoveConfirm = async (targetFolderID: string | null) => {
    await folder.moveItems(selectedKnowledgeIDs, selectedFileDocIDs, targetFolderID);
    setSelectedKnowledgeIDs([]);
    setSelectedFileDocIDs([]);
    setMoveModalOpen(false);
  };

  // Use folder contents when a folder is selected, otherwise fall back to the global list
  const selectedFolder = folder.folderContents?.folder ?? null;
  const displayKnowledge = folder.folderContents?.knowledge ?? state.knowledgeList;
  const displayFileDocs = folder.folderContents?.file_documents ?? fileDocuments;
  const subFolders = folder.folderContents?.sub_folders ?? [];
  const breadcrumb = folder.folderContents?.breadcrumb ?? [];
  const isLoading = folder.loadingContents || state.loading;

  const handleDeleteCurrentFolder = () => {
    if (!selectedFolder) return;

    Modal.confirm({
      title: '確認刪除資料夾',
      content: `確定要刪除資料夾「${selectedFolder.name}」嗎？資料夾內的所有子資料夾、知識條目、檔案文件及相關向量資料都會被永久刪除。`,
      okText: '確認刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => folder.deleteFolder(selectedFolder.id),
    });
  };

  // Auto-refresh when there are processing items (poll every 5s)
  const hasProcessing = displayFileDocs.some(d => d.processing_status === 'processing' || d.processing_status === 'pending');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (hasProcessing) {
      pollRef.current = setInterval(() => {
        folder.loadContents(folder.selectedFolderID);
        folder.loadTree();
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasProcessing, folder.selectedFolderID]);

  // Column definitions
  const columns: ColumnsType<Knowledge> = [
    {
      title: '',
      key: 'select',
      width: 40,
      render: (_, record: Knowledge) => (
        <Checkbox
          checked={selectedKnowledgeIDs.includes(record.id)}
          onChange={(e) => {
            setSelectedKnowledgeIDs(prev =>
              e.target.checked ? [...prev, record.id] : prev.filter(id => id !== record.id)
            );
          }}
        />
      ),
    },
    {
      title: '標題',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 240,
      render: (title: string, record: Knowledge) => (
        <Tooltip title={title} placement="topLeft">
          <Button
            type="link"
            style={{ padding: 0, fontWeight: 600, textAlign: 'left', whiteSpace: 'normal', height: 'auto' }}
            onClick={() => handleView(record.id)}
          >
            {title}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 110,
      render: (author: string) => (
        <Text style={{ fontSize: 13 }}>{author || '-'}</Text>
      ),
    },
    {
      title: '標籤',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => {
        if (!tags || tags.length === 0) return <Text type="secondary">&mdash;</Text>;
        return (
          <Space size={4} wrap>
            {tags.map(tag => (
              <Tag key={tag} color={tagColor(tag)} style={{ margin: 0 }}>
                {tag}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '瀏覽次數',
      dataIndex: 'view_count',
      key: 'view_count',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.view_count || 0) - (b.view_count || 0),
      render: (count: number) => (
        <Text style={{ fontSize: 13 }}>{count ?? 0}</Text>
      ),
    },
    {
      title: 'LLM Tokens',
      dataIndex: 'llm_tokens',
      key: 'llm_tokens',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.llm_tokens || 0) - (b.llm_tokens || 0),
      render: (tokens: number | undefined | null) => {
        const v = tokens || 0;
        return (
          <Text style={{ fontSize: 13, color: v > 0 ? '#1677ff' : '#bfbfbf' }}>
            {v > 0 ? v.toLocaleString() : '\u2014'}
          </Text>
        );
      },
    },
    {
      title: '成本 (USD)',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.total_cost || 0) - (b.total_cost || 0),
      render: (cost: number | undefined | null) => {
        const v = cost || 0;
        return (
          <Text
            style={{
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
              color: v > 0 ? '#52c41a' : '#bfbfbf',
            }}
          >
            {v > 0 ? formatCostUSD(v) : '\u2014'}
          </Text>
        );
      },
    },
    {
      title: '建立日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateShort(date)}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record: Knowledge) => (
        <Space size={4}>
          <Tooltip title="檢視">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record.id)}
            />
          </Tooltip>
          <Tooltip title="編輯">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.id)}
            />
          </Tooltip>
          <Tooltip title="刪除">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const hasSelection = selectedKnowledgeIDs.length > 0 || selectedFileDocIDs.length > 0;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 220px)', gap: 0 }}>
      {/* Left: Folder Tree */}
      <div
        style={{
          width: 260,
          minWidth: 200,
          borderRight: '1px solid #f0f0f0',
          background: '#fafafa',
          borderRadius: '8px 0 0 8px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FolderTree
          tree={folder.folderTree}
          selectedKey={folder.selectedFolderID}
          expandedKeys={folder.expandedKeys}
          loading={folder.loadingTree}
          onSelect={folder.selectFolder}
          onExpandedKeysChange={folder.setExpandedKeys}
          onCreate={folder.createFolder}
          onRename={folder.renameFolder}
          onDelete={folder.deleteFolder}
        />
      </div>

      {/* Right: Folder Contents */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {/* Breadcrumb */}
        <FolderBreadcrumb breadcrumb={breadcrumb} onNavigate={folder.selectFolder} />

        <Card
          size="small"
          style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: 16 }}
        >
          {/* Controls row */}
          <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                新增知識
              </Button>
            </Col>
            {selectedFolder && (
              <Col>
                <Tooltip title="刪除目前資料夾">
                  <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCurrentFolder}>
                    刪除資料夾
                  </Button>
                </Tooltip>
              </Col>
            )}
            {hasSelection && (
              <Col>
                <Button icon={<SwapOutlined />} onClick={() => setMoveModalOpen(true)}>
                  移動到資料夾 ({selectedKnowledgeIDs.length + selectedFileDocIDs.length})
                </Button>
              </Col>
            )}
            <Col flex="auto">
              <Row gutter={8} justify="end" align="middle">
                <Col xs={24} sm={10} md={8}>
                  <Search
                    placeholder="搜尋標題或關鍵字"
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: '100%' }}
                    size="small"
                  />
                </Col>
                <Col xs={24} sm={10} md={8}>
                  <Select
                    mode="multiple"
                    placeholder="篩選標籤"
                    allowClear
                    style={{ width: '100%' }}
                    size="small"
                    maxTagCount="responsive"
                    onChange={handleTagFilter}
                    options={state.allTags.map(tag => ({ label: tag, value: tag }))}
                  />
                </Col>
                <Col>
                  <Tooltip title="重新整理">
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        folder.loadTree();
                        folder.loadContents(folder.selectedFolderID);
                      }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Sub-folders grid */}
          {subFolders.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>
                子資料夾
              </Text>
              <Row gutter={[8, 8]}>
                {subFolders.map((sf) => {
                  const totalCount = sf.knowledge_count + sf.file_document_count;
                  return (
                    <Col key={sf.id}>
                      <Button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          height: 'auto',
                          padding: '6px 12px',
                        }}
                        onClick={() => folder.selectFolder(sf.id)}
                      >
                        <FolderOutlined style={{ color: '#faad14', fontSize: 16 }} />
                        <span>{sf.name}</span>
                        {totalCount > 0 && (
                          <Badge
                            count={totalCount}
                            size="small"
                            style={{ backgroundColor: '#d9d9d9', color: '#595959' }}
                          />
                        )}
                      </Button>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}

          {/* File documents section */}
          {displayFileDocs.length > 0 && (
            <Card
              size="small"
              title="檔案來源知識"
              style={{ marginBottom: 12, borderRadius: 8 }}
              loading={loadingFiles}
            >
              <List
                dataSource={displayFileDocs}
                renderItem={(doc: FileDocument) => {
                  const isImage = imageTypes.includes(doc.file_type);
                  const isProcessing = doc.processing_status === 'processing' || doc.processing_status === 'pending';
                  return (
                    <List.Item
                      key={doc.id}
                      actions={[
                        <Checkbox
                          key="sel"
                          checked={selectedFileDocIDs.includes(doc.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedFileDocIDs(prev =>
                              e.target.checked ? [...prev, doc.id] : prev.filter(id => id !== doc.id)
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />,
                        <Button
                          key="view"
                          type="link"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setViewFileDocId(doc.id); }}
                        >
                          查看詳情
                        </Button>,
                        <Button
                          key="delete"
                          type="link"
                          size="small"
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            Modal.confirm({
                              title: '確認刪除',
                              content: `刪除「${doc.original_filename}」及其所有知識點？`,
                              okText: '刪除',
                              okButtonProps: { danger: true },
                              cancelText: '取消',
                              onOk: () => handleDeleteFile(doc.id),
                            });
                          }}
                        >
                          刪除
                        </Button>,
                      ]}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setViewFileDocId(doc.id)}
                    >
                      <List.Item.Meta
                        avatar={
                          isProcessing
                            ? <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} />} />
                            : fileTypeIcon(doc.file_type)
                        }
                        title={
                          <Space>
                            <span>{doc.original_filename}</span>
                            {doc.processing_status === 'completed' ? (
                              <>
                                {doc.knowledge_count > 0 && (
                                  <Badge
                                    count={`${doc.knowledge_count} 個知識點`}
                                    style={{ backgroundColor: '#1677ff' }}
                                  />
                                )}
                                <Tag color="green">已完成</Tag>
                              </>
                            ) : doc.processing_status === 'failed' ? (
                              <Tag color="red">處理失敗</Tag>
                            ) : (
                              <Tag icon={<LoadingOutlined />} color="processing">
                                處理中...
                              </Tag>
                            )}
                          </Space>
                        }
                        description={
                          isProcessing
                            ? '正在進行文字抽取、AI 分析與向量化，完成後自動更新...'
                            : `${doc.page_count} 頁 · ${formatDateShort(doc.created_at)}`
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>
          )}

          {/* Knowledge table */}
          <Table<Knowledge>
            rowKey="id"
            columns={columns}
            dataSource={displayKnowledge}
            loading={isLoading}
            scroll={{ x: 'max-content' }}
            size="small"
            pagination={{
              current: folder.contentsPagination.current,
              pageSize: folder.contentsPagination.pageSize,
              total: folder.contentsPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                `第 ${range[0]}\u2013${range[1]} 項，共 ${total} 項`,
              onChange: (page, pageSize) => {
                folder.setContentsPagination(prev => ({ ...prev, current: page, pageSize }));
                folder.loadContents(folder.selectedFolderID, page, pageSize);
              },
            }}
            locale={{ emptyText: '此資料夾尚無知識條目。' }}
          />
        </Card>
      </div>

      {/* Move to folder modal */}
      <MoveToFolderModal
        open={moveModalOpen}
        tree={folder.folderTree}
        currentFolderID={folder.selectedFolderID}
        onOk={handleMoveConfirm}
        onCancel={() => setMoveModalOpen(false)}
      />

      {/* Create knowledge modal */}
      <CreateKnowledgeModal
        open={createModalOpen}
        folderID={folder.selectedFolderID}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCreated}
      />

      {/* Knowledge detail modal */}
      <KnowledgeDetailModal
        open={viewKnowledgeId !== null}
        knowledgeId={viewKnowledgeId}
        onClose={() => setViewKnowledgeId(null)}
        onDeleted={handleCreated}
      />

      {/* File document detail modal */}
      <FileDocumentDetailModal
        open={viewFileDocId !== null}
        fileDocId={viewFileDocId}
        onClose={() => setViewFileDocId(null)}
        onDeleted={handleCreated}
        onKnowledgeClick={(kid) => { setViewFileDocId(null); setViewKnowledgeId(kid); }}
      />
    </div>
  );
};

export default KnowledgeList;
